// ─────────────────────────────────────────────
//  LocumList — bottom of the Timeline Chart. Every MD-call doctor flagged as
//  a locum ("L" designation) with a "Leaving Time" select beside their name.
//  Picking a time LOCKS it (persisted to every facility board — the MD call
//  list is facility-wide) and the staffing adequacy math immediately stops
//  counting the locum from the first shift boundary at/after that time. At
//  the scheduled time the page's clock effect removes them from the MD call
//  roster on all facilities outright.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import { setLocumLeaveTime } from "../firebase/firestore";
import { to12h, hmToMinutes } from "../domain/timeline";
import tokens from "../theme/tokens";

const { color, font, radius } = tokens;

// Half-hour leave slots across the working day (11:00 → 23:00).
const LEAVE_OPTIONS = [];
for (let h = 11; h <= 23; h++) {
  LEAVE_OPTIONS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 23) LEAVE_OPTIONS.push(`${String(h).padStart(2, "0")}:30`);
}

export default function LocumList({ locums = [], leaveTimes = {}, date, uid, canEdit = false, nowMin = null }) {
  const toast = useToast();
  const [busyCode, setBusyCode] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!locums.length) return null;

  // ⚠️ THE CARD EARNS ITS SPACE ONLY WHILE SOMETHING IS UNDECIDED. Owner,
  // 2026-09-09: "after all locum are locked with the times, minimize this box".
  //
  // Every locum with a locked leaving time means there is nothing left to set,
  // and this is the largest card on a panel that also has to show breaks,
  // messages and the doctor plan. Collapsed it keeps the one fact worth keeping
  // — how many, and when the first one goes — and gives the height back.
  //
  // ⚠️ IT COLLAPSES, IT DOES NOT DISAPPEAR. These times drop a doctor off MD
  // Call on every facility, so the card must stay reachable to be corrected: a
  // locked time somebody wants to change is exactly the case where hiding it
  // outright would send them hunting.
  const settled = locums.filter((l) => leaveTimes[l.code]);
  const allLocked = settled.length === locums.length;
  const collapsed = allLocked && !expanded;

  // The earliest departure, because that is the one that changes the board
  // first and the only time worth printing in a single line.
  const firstOutMin = settled
    .map((l) => hmToMinutes(leaveTimes[l.code]))
    .filter((m) => m != null)
    .sort((a, b) => a - b)[0];

  const setLeave = async (code, name, hm) => {
    setBusyCode(code);
    try {
      await setLocumLeaveTime(date, code, hm, uid);
      toast.success(hm ? `${name} leaves at ${to12h(hmToMinutes(hm))} — locked on all boards.` : `${name}'s leaving time cleared.`);
    } catch (e) {
      console.error("Set locum leave failed:", e);
      toast.error("Could not save the leaving time.");
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <div style={st.wrap}>
      {/* ⚠️ AN h2, LIKE EVERY OTHER CARD IN THIS PANEL. It was a plain div, so
          a screen-reader user navigating the panel by heading passed straight
          over the one card that changes who is on MD call. Same inline style,
          no visual change. */}
      <h2 style={st.title}>
        Locum List
        {collapsed ? (
          <span style={st.note}>
            {" — "}{locums.length} locked
            {firstOutMin != null ? `, first out ${to12h(firstOutMin)}` : ""}
          </span>
        ) : (
          <span style={st.note}> — set a leaving time; at that time they drop off MD Call on every facility and the staffing counts above exclude them from that shift onward</span>
        )}
        {allLocked && (
          <button
            type="button"
            style={st.toggle}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            data-testid="locum-toggle"
          >
            {expanded ? "Hide" : "Show"}
          </button>
        )}
      </h2>
      {!collapsed && (
      <div style={st.grid}>
        {locums.map((l) => {
          const hm = leaveTimes[l.code] || "";
          const leaveMin = hmToMinutes(hm);
          const leavingSoon = nowMin != null && leaveMin != null && leaveMin - nowMin <= 30 && leaveMin > nowMin;
          return (
            <div key={l.code} style={st.row}>
              <span style={st.badge}>L</span>
              <span style={st.name} title={l.code}>{l.name}</span>
              <label style={st.leaveLabel}>
                Leaving
                <select
                  style={{ ...st.select, ...(hm ? st.selectLocked : {}) }}
                  value={hm}
                  disabled={!canEdit || busyCode === l.code}
                  onChange={(e) => setLeave(l.code, l.name, e.target.value)}
                  aria-label={`Leaving time for ${l.name}`}
                >
                  <option value="">— not set —</option>
                  {hm && !LEAVE_OPTIONS.includes(hm) && <option value={hm}>{to12h(hmToMinutes(hm))}</option>}
                  {LEAVE_OPTIONS.map((t) => (
                    <option key={t} value={t}>{to12h(hmToMinutes(t))}</option>
                  ))}
                </select>
              </label>
              {hm && (
                <span style={{ ...st.locked, ...(leavingSoon ? { color: color.accent.error } : {}) }}>
                  {leavingSoon ? "⚠ leaving soon" : "🔒 locked"}
                </span>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

const st = {
  // ⚠️ NO `marginTop`. It carried 16px for the days this card was a sibling
  // dropped at the foot of the page; it is now a cell in the side panel's
  // grid, which owns the 12px rhythm, and the margin showed as a card that
  // would not sit flush with its row.
  wrap: { background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: 8, padding: "12px 14px" },
  title: { margin: 0, fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary, marginBottom: 10 },
  note: { fontSize: font.size.label, fontWeight: font.weight.regular, color: color.text.muted },
  // 44px high and pushed to the end of the heading row: it is a control on a
  // card a board-runner reaches for mid-shift, not a decoration.
  toggle: {
    float: "right",
    minHeight: 44,
    padding: "0 10px",
    borderRadius: radius.sm,
    border: `1px solid ${color.border.default}`,
    background: color.surface.page,
    color: color.text.muted,
    font: "inherit",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  // ⚠️ 320, NOT 280, AND IT WAS MEASURED. A row is badge + name + "Leaving" +
  // a time select, and only the name can shrink. At a 280 floor the panel's
  // 1184px fitted FOUR tracks of ~278px, which left the name ~80px and printed
  // "Andrew Lueb…" — worse at 1120 than at 900, because the wider panel bought
  // another column rather than a wider one. 320 keeps the count at three and
  // the names whole.
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 8 },
  // `flexWrap` is the backstop for a name longer than any on the board today:
  // the control drops to a second line rather than the name losing its letters.
  row: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "6px 10px", minWidth: 0 },
  badge: { flexShrink: 0, background: "#0e7490", color: "#fff", borderRadius: 9999, width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 },
  // `minWidth: 110` is what makes the wrap above fire. A flex item with
  // `minWidth: 0` shrinks to nothing before its siblings give up a pixel, which
  // is exactly how the name ended up as an ellipsis; with a floor, the row
  // wraps instead.
  name: { fontSize: 13, fontWeight: font.weight.semibold, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 110, flex: 1 },
  leaveLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: font.weight.bold, color: color.text.secondary, flexShrink: 0 },
  select: { background: "#fff", border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: radius.sm, padding: "5px 8px", fontSize: 12, fontFamily: "inherit" },
  selectLocked: { borderColor: color.accent.primary, fontWeight: 700 },
  locked: { flexShrink: 0, fontSize: 11, fontWeight: font.weight.bold, color: color.accent.primary },
};
